/**
 * Home's sidebar. There is no Chat page: New chat (whose magnifier
 * filters Recents), Insights for organization admins, a More row holding
 * Skills, Knowledge, Plugins and Automations, then the Apps, Spaces and
 * Recents sections. Recents is the organization's whole chat history. Below
 * md, where the rail and top strip are hidden, it also carries the org
 * switcher and the footer's Search, Governance, Usage and Settings.
 *
 * @module components/home/HomeSidebar
 */
import { scopeProjectRef } from "@amoreg/client-runtime/environment";
import { Link, useLocation } from "@tanstack/react-router";
import { BlocksIcon, BookOpenIcon, CalendarClockIcon, ChartColumnIcon, ScrollTextIcon } from "lucide-react";
import { useCallback, useMemo, useState } from "react";

import { useNewThreadHandler } from "../../hooks/useHandleNewThread";
import { orgUrlSegment } from "../../orgRouting";
import { useProjects } from "../../state/entities";
import { usePrimaryEnvironmentId } from "../../state/environments";
import { useActiveOrg, useActiveOrgId } from "../../state/orgs";
import OrgSwitcher from "../OrgSwitcher";
import { SidebarChromeFooter } from "../sidebar/SidebarChrome";
import { SidebarContent, SidebarGroup, SidebarMenu, SidebarMenuItem, useSidebar } from "../ui/sidebar";
import { SidebarRow } from "../ui/sidebar-row";
import { SidebarSection } from "../ui/sidebar-section";
import { HomeSidebarApps } from "./HomeSidebarApps";
import { HomeSidebarNewChatRow } from "./HomeSidebarNewChatRow";
import { activeHomeNavRow, moreHoldsOpen } from "./HomeSidebar.logic";
import { HomeSidebarRecents } from "./HomeSidebarRecents";
import { HomeSidebarSpaces } from "./HomeSidebarSpaces";
import { HomeSidebarMore, useHomeSidebarSectionOpen } from "./HomeSidebarSection";

export function HomeSidebar() {
  const pathname = useLocation({ select: (location) => location.pathname });
  const orgId = useActiveOrgId();
  const activeOrg = useActiveOrg();
  const showsInsights = activeOrg !== null && activeOrg.role !== "member";
  const environmentId = usePrimaryEnvironmentId();
  const projects = useProjects();
  const handleNewThread = useNewThreadHandler();
  const { isMobile, setOpenMobile } = useSidebar();
  const orgSegment = orgId ? orgUrlSegment(orgId) : null;
  const orgProject = useMemo(
    () => projects.find((project) => project.id === orgId && project.environmentId === environmentId) ?? null,
    [environmentId, orgId, projects],
  );
  const closeOnMobile = useCallback(() => {
    if (isMobile) setOpenMobile(false);
  }, [isMobile, setOpenMobile]);
  // The Recents filter text, or null while the row is New chat.
  const [filter, setFilter] = useState<string | null>(null);
  const filtering = filter !== null && filter.trim() !== "";
  const [recentsOpen, setRecentsOpen] = useHomeSidebarSectionOpen("recents", filtering);
  const activeRow = activeHomeNavRow(pathname);
  const newChat = () => {
    if (!orgProject) return;
    closeOnMobile();
    void handleNewThread(scopeProjectRef(orgProject.environmentId, orgProject.id));
  };

  return (
    <>
      <SidebarContent className="overflow-x-hidden" data-home-sidebar="">
        <SidebarGroup className="gap-5 p-[var(--sidebar-content-inset)] pt-2.5">
          <div className="flex flex-col gap-0.5" data-home-nav="">
            {/* The org switcher sits in the top strip from md; below md the strip is hidden. */}
            {isMobile ? <OrgSwitcher /> : null}
            <HomeSidebarNewChatRow
              onNewChat={newChat}
              newChatDisabled={orgProject === null}
              newChatActive={pathname.includes("/draft/")}
              filter={filter}
              onFilterChange={setFilter}
            />
            {orgSegment ? (
              <SidebarMenu className="gap-0.5 ps-px">
                {showsInsights ? (
                  <SidebarMenuItem>
                    <SidebarRow
                      isActive={activeRow === "insights"}
                      render={<Link to="/$org/command-center" params={{ org: orgSegment }} onClick={closeOnMobile} />}
                    >
                      <ChartColumnIcon />
                      <span className="truncate">Insights</span>
                    </SidebarRow>
                  </SidebarMenuItem>
                ) : null}
                <SidebarMenuItem>
                  <HomeSidebarMore holdOpen={moreHoldsOpen(pathname)}>
                    <SidebarMenuItem>
                      <SidebarRow
                        isActive={activeRow === "skills"}
                        render={<Link to="/$org/skills" params={{ org: orgSegment }} onClick={closeOnMobile} />}
                      >
                        <ScrollTextIcon />
                        <span className="truncate">Skills</span>
                      </SidebarRow>
                    </SidebarMenuItem>
                    <SidebarMenuItem>
                      <SidebarRow
                        render={
                          <Link to="/$org/compliance/knowledge" params={{ org: orgSegment }} onClick={closeOnMobile} />
                        }
                      >
                        <BookOpenIcon />
                        <span className="truncate">Knowledge</span>
                      </SidebarRow>
                    </SidebarMenuItem>
                    <SidebarMenuItem>
                      <SidebarRow
                        data-home-nav-row="plugins"
                        isActive={activeRow === "plugins"}
                        render={<Link to="/$org/connections" params={{ org: orgSegment }} onClick={closeOnMobile} />}
                      >
                        <BlocksIcon />
                        <span className="truncate">Plugins</span>
                      </SidebarRow>
                    </SidebarMenuItem>
                    <SidebarMenuItem>
                      <SidebarRow
                        data-home-nav-row="automations"
                        isActive={activeRow === "automations"}
                        render={<Link to="/$org/automations" params={{ org: orgSegment }} onClick={closeOnMobile} />}
                      >
                        <CalendarClockIcon />
                        <span className="truncate">Automations</span>
                      </SidebarRow>
                    </SidebarMenuItem>
                  </HomeSidebarMore>
                </SidebarMenuItem>
              </SidebarMenu>
            ) : null}
          </div>
          <HomeSidebarApps onNavigate={closeOnMobile} />
          <HomeSidebarSpaces onNavigate={closeOnMobile} />
          <SidebarSection label="Recents" open={recentsOpen} onOpenChange={setRecentsOpen}>
            <HomeSidebarRecents query={filter ?? ""} onNavigate={closeOnMobile} />
          </SidebarSection>
        </SidebarGroup>
      </SidebarContent>
      <SidebarChromeFooter />
    </>
  );
}
